/**
 * The arithmetic behind `YearWindowSelector`, kept out of the component file.
 *
 * The caller needs the same clamping the control uses — it has to slice its own
 * array to match what the picker says is on screen — and two copies of that
 * would drift the first time either changed.
 */

export const YEAR_WINDOW_OPTIONS = [3, 5, 7, 10] as const;

export type YearWindowSize = (typeof YEAR_WINDOW_OPTIONS)[number];

/**
 * Clamp a window to the data.
 *
 * Applied at render rather than stored clamped, so shrinking the span — or
 * switching to a source with fewer years — cannot leave the window pointing
 * past the end of the data.
 */
export function clampWindow(
  totalYears: number,
  size: number,
  offset: number
): { size: number; offset: number } {
  const boundedSize = Math.max(1, Math.min(size, Math.max(1, totalYears)));
  const maxOffset = Math.max(0, totalYears - boundedSize);
  return { size: boundedSize, offset: Math.max(0, Math.min(offset, maxOffset)) };
}

/**
 * Is the picker worth showing at all?
 *
 * With three years or fewer every option is wider than the data, so the control
 * would be four struck-through buttons that do nothing — which is exactly what
 * a two-period PDF chain would render.
 */
export function yearWindowWorthShowing(totalYears: number): boolean {
  return totalYears > YEAR_WINDOW_OPTIONS[0];
}
